/**
 * Client-side maintenance routing.
 *
 * See docs/announcements-maintenance.md ("Build-time vs. browser vs. edge
 * behavior") for why this is a client-side check rather than an edge/Worker
 * one, and for the flash-of-content tradeoff that comes with that choice.
 *
 * Re-checks on every route change (so a client-side navigation into an active
 * maintenance window is caught) and on a timer (so a tab left open across a
 * scheduled start/end time transitions without needing a reload).
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import siteConfig from '@/data/site-config.json'
import { getMaintenanceStatus } from '@/lib/maintenance'

/** Reachable even during an outage: the maintenance page itself, and support. */
const EXEMPT_PATHS = new Set(['/maintenance', '/contact'])

/** How often an already-open tab re-evaluates the schedule. */
const RECHECK_INTERVAL_MS = 30_000

export const MaintenanceGate = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), RECHECK_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    const status = getMaintenanceStatus(siteConfig.maintenance)
    const exempt = EXEMPT_PATHS.has(pathname)

    if (status.active && !exempt) {
      navigate('/maintenance', { replace: true })
    } else if (!status.active && pathname === '/maintenance') {
      navigate('/', { replace: true })
    }
    // `tick` is intentionally a dependency with no other purpose: it forces this
    // effect to re-run on the timer above without needing its own duplicate logic.
  }, [pathname, tick, navigate])

  return <>{children}</>
}
